import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type DriverDocumentsAdminView, type KycDocumentTypeWire, type MarketDriverStanding, type ReviewedDocumentView } from '@vello/api-client';
import { REVIEW_NOTE_MAX, DOCUMENT_NUMBER_MAX, splitReason } from '@vello/driver-eligibility';
import {
  DOCUMENT_REVIEW_RESULTS,
  cashHandoverActor,
  documentReasonOptions,
  documentReviewInput,
  documentValidityPreview,
  driverEnablementAuthority,
  eligibilityRows,
  emptyDocumentLineDraft,
  formatMarketInstant,
  latestLineByType,
  loadDriverDocuments,
  loadMarketDrivers,
  marketTimeZone,
  recordDocumentReview,
  reviewLineTone,
  reviewableTypes,
  tailOf,
  withDocumentResult,
  type DocumentLineDraft,
  type DocumentReviewResult,
  type EligibilityRow,
} from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { documentTypeName } from '../i18n/document-type';
import { MarketField } from '../components/market-field';
import { PersonName, usePersonName, usePersonNames } from '../components/person-name';
import { RevealContact } from '../components/reveal-contact';
import { usePhoneText } from '../components/phone-text';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, Field, Notice, PageHeader, Panel, Select, Table, TextInput, FormGrid, EmptyState, InlineStack, Text, Ink, Row, Stack, EmptyCell, RecordSection } from '@vello/web-theme';
import { LoadingBlock } from '../components/loading-block';
import { DrawerStack } from '../components/drawer-stack';

type Msg = { readonly tone: 'success' | 'danger' | 'warning'; readonly text: string };

/**
 * **Motoristas › Documentos** (W8-DRIVER-ELIGIBILITY, D437, ADR-0069) — the assisted IN-PERSON review: the driver brings
 * the originals to the operator, who checks each document and records one line per type in the Admin. Nothing is
 * uploaded until the native camera exists.
 *
 * - The **market** is chosen from the operator's scope (never typed); the market's drivers (`GET /v1/admin/driver-
 *   eligibility`, `document:view`) are listed with the last digits of their phone and where each stands per service
 *   category and as a courier, with the reasons in words (a document reason names the type by the catalog's label).
 * - A driver's **documents** (`GET /v1/admin/drivers/:id/documents`): every active catalog type (those required in the
 *   market first), the latest line of each (masked number, validity, result, reason, who checked, when) and the history.
 * - One **review line** per type (`POST …/documents/reviews`, `document:review` — the KYC reviewer: GLOBAL or the
 *   driver's COUNTRY): the number when the type records one, the validity date when it has one (the date is sent as the
 *   end of that day in UTC), approve or reject with a reason from the reason catalog (a note for `OTHER`), the driver's
 *   vehicle for a vehicle document. A refusal (`NUMBER_REQUIRED`, `EXPIRY_IN_PAST`, …) is explained in words.
 * - A refused read is an error, never an empty list. No native dialog is ever opened.
 */
export function DriverDocumentsScreen(): ReactElement {
  const { runtime, markets, me, grants } = useAdmin();
  const { t, lang } = useT();
  const { fail, v, refusal: refusalOf } = useVocab();
  const operatorId = me?.id;
  const { mayReview } = driverEnablementAuthority(grants);
  const phoneOf = usePhoneText();
  const [market, setMarket] = useState('');
  const [drivers, setDrivers] = useState<readonly MarketDriverStanding[] | undefined>(undefined);
  const [loadedMarket, setLoadedMarket] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [driverId, setDriverId] = useState<string | undefined>(undefined);
  const [docs, setDocs] = useState<DriverDocumentsAdminView | undefined>(undefined);
  const [docsError, setDocsError] = useState<string | undefined>(undefined);
  const [drafts, setDrafts] = useState<Readonly<Record<string, DocumentLineDraft>>>({});
  const [lineMsg, setLineMsg] = useState<{ readonly type: string; readonly msg: Msg } | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  /** The catalog types seen in a driver's documents: the list's document reasons are named by their labels once known. */
  const [knownTypes, setKnownTypes] = useState<readonly KycDocumentTypeWire[]>([]);
  const listSeq = useRef(0);
  const docsSeq = useRef(0);

  const refusal = (e: { reasonCode?: string; detail?: string; code: string }, fallbackKey: string): string => {
    return refusalOf('refusal.admin.driver_enablement', e) ?? t(fallbackKey, { code: fail(e) });
  };

  const loadList = useCallback(
    async (m: string, background = false): Promise<void> => {
      const n = ++listSeq.current;
      if (!background) {
        setError(undefined);
        setLoadedMarket(m);
      }
      if (m === '') {
        setDrivers([]);
        return;
      }
      if (!background) setDrivers(undefined);
      const res = await loadMarketDrivers(runtime, m);
      if (n !== listSeq.current) return;
      if (res.ok) {
        setError(undefined);
        setDrivers(res.value.drivers);
      } else setError(refusal(res.error, 'denable.docs.error.list'));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runtime, t],
  );

  const loadDocs = useCallback(
    async (id: string): Promise<void> => {
      const n = ++docsSeq.current;
      setDocsError(undefined);
      const res = await loadDriverDocuments(runtime, id);
      if (n !== docsSeq.current) return;
      if (res.ok) {
        setDocs(res.value);
        setKnownTypes(res.value.types);
      } else {
        setDocs(undefined);
        setDocsError(refusal(res.error, 'denable.docs.error.driver'));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runtime, t],
  );

  // The list follows the scoped market by itself and stays current in the background (no "load" button).
  useEffect(() => {
    void loadList(market);
  }, [market, loadList]);
  useAutoRefresh(() => loadList(market, true), { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== '' });

  const pick = async (id: string): Promise<void> => {
    setDriverId(id);
    setDocs(undefined);
    setDrafts({});
    setLineMsg(undefined);
    await loadDocs(id);
  };

  const tz = marketTimeZone(docs?.marketId ?? loadedMarket, markets);
  const when = (instant: string | undefined): string => formatMarketInstant(instant, tz, lang);
  /** A document's validity is a calendar day on the market's wall clock (sent as its end there, W8E-02): shown in that zone. */
  const validity = (instant: string | undefined): string => (instant === undefined ? '—' : formatMarketInstant(instant, tz, lang, 'date'));
  const personName = usePersonNames((docs?.lines ?? []).map((l) => l.reviewedBy));
  const who = (id: string | undefined): string => {
    const actor = cashHandoverActor(id ?? null, undefined, operatorId);
    return actor === undefined ? '—' : actor.kind === 'you' ? t('denable.actor.you') : (personName(id) ?? actor.text);
  };
  const types = docs?.types ?? [];
  const typeLabel = (code: string): string => documentTypeName(types, code, lang, v);

  const reasonText = (reason: string, pool: readonly Pick<KycDocumentTypeWire, 'code' | 'labels'>[]): string => {
    const parts = splitReason(reason);
    return v('driver.eligibility_reason', parts.code, { type: parts.arg !== undefined ? documentTypeName(pool, parts.arg, lang, v) : '' });
  };
  const contextLabel = (r: EligibilityRow): string => (r.kind === 'courier' ? t('denable.context.courier') : t('denable.context.category', { category: v('ride.category', r.category) }));
  const standing = (rows: readonly EligibilityRow[], pool: readonly Pick<KycDocumentTypeWire, 'code' | 'labels'>[]): ReactElement => (
    <InlineStack gap={1}>
      {rows.map((r) => (
        <span key={r.key}>
          <Badge tone={r.eligible ? 'success' : 'danger'}>{t(r.eligible ? 'denable.eligible.yes' : 'denable.eligible.no', { context: contextLabel(r) })}</Badge>
          {!r.eligible && r.reasons.length > 0 && <Text role="caption" tone="tertiary" block>{r.reasons.map((x) => reasonText(x, pool)).join(' · ')}</Text>}
        </span>
      ))}
    </InlineStack>
  );

  const draftOf = (code: string): DocumentLineDraft => drafts[code] ?? emptyDocumentLineDraft();
  const setDraft = (code: string, next: DocumentLineDraft): void => setDrafts({ ...drafts, [code]: next });

  const record = async (type: KycDocumentTypeWire): Promise<void> => {
    if (docs === undefined) return;
    setBusy(true);
    setLineMsg(undefined);
    const res = await recordDocumentReview(runtime, docs.driverId, documentReviewInput(type, draftOf(type.code), tz));
    setBusy(false);
    if (!res.ok) {
      setLineMsg({ type: type.code, msg: { tone: 'danger', text: refusal(res.error, 'ui.actionFailed') } });
      return;
    }
    setLineMsg({ type: type.code, msg: { tone: 'success', text: t(res.value.document.status === 'APPROVED' ? 'denable.docs.recorded.approved' : 'denable.docs.recorded.rejected', { type: kycLabel(type) }) } });
    setDrafts({ ...drafts, [type.code]: emptyDocumentLineDraft() });
    await Promise.all([loadDocs(docs.driverId), loadedMarket !== undefined && loadedMarket !== '' ? loadList(loadedMarket) : Promise.resolve()]);
  };
  const kycLabel = (type: Pick<KycDocumentTypeWire, 'code' | 'labels'>): string => documentTypeName([type], type.code, lang, v);

  const latest = docs !== undefined ? latestLineByType(docs.lines) : new Map<string, ReviewedDocumentView>();
  const plateOf = (vehicleId: string): string => docs?.vehicles.find((v) => v.vehicle.id === vehicleId)?.vehicle.plate ?? vehicleId;
  const statusLabel = (s: string): string => v('kyc.eligibility_line_status', s);
  // D619: the registered name when there is one, else the account's name (batched lookup), else the short id.
  const hasName = (d: { readonly displayName: string | null }): d is { readonly displayName: string } => d.displayName !== null && d.displayName.trim() !== '';
  const docsDriverName = usePersonName(docs?.driverId);
  const driverName = (d: { readonly displayName: string | null; readonly driverId: string }): string => (hasName(d) ? d.displayName : (docsDriverName ?? tailOf(d.driverId)));

  return (
    <>
      <PageHeader title={t('denable.docs.title')} subtitle={t('denable.docs.subtitle')} />

      <Panel><Stack gap={4}>
        <div>
          <Notice tone="neutral">{t('denable.docs.inPerson')}</Notice>
        </div>
        <MarketField id="docs-market" value={market} onChange={setMarket} />
        {loadedMarket === '' && <Notice tone="neutral">{t('denable.marketHint')}</Notice>}
        {error !== undefined && <Notice tone="danger">{error}</Notice>}
        {!mayReview && <Notice tone="neutral">{t('denable.docs.noReview')}</Notice>}
      </Stack></Panel>

      <section aria-label={t('denable.docs.drivers')}>
        <Panel padded={false}>
          <Table
            columns={[
              {
                key: 'driver',
                header: t('denable.col.driver'),
                render: (d: MarketDriverStanding) => (
                  <span>
                    {hasName(d) ? d.displayName : <PersonName id={d.driverId} />}
                    {d.phoneLast4 !== null && <Text role="caption" tone="tertiary" mono block>{phoneOf(`•••• ${d.phoneLast4}`)}</Text>}
                  </span>
                ),
              },
              { key: 'standing', header: t('denable.col.standing'), render: (d: MarketDriverStanding) => standing(eligibilityRows(d), knownTypes) },
            ]}
            rows={drivers ?? []}
            rowKey={(d) => d.driverId}
            onRowClick={(d) => void pick(d.driverId)}
            selectedKey={driverId}
            loading={loadedMarket !== undefined && loadedMarket !== '' && drivers === undefined && error === undefined}
            loadingLabel={t('ui.loading')}
            error={error !== undefined ? t('ui.loadFailed') : undefined}
            onRetry={() => void loadList(market)}
            retryLabel={t('ui.retry')}
            empty={<EmptyState title={loadedMarket === undefined || loadedMarket === '' ? t('denable.marketHint') : t('denable.docs.driversEmpty')} />}
          />
        </Panel>
      </section>

      <Drawer open={driverId !== undefined} onClose={() => setDriverId(undefined)} labelledBy="ddocs-detail-title">
        {driverId !== undefined && (
          <>
            <DrawerHeader titleId="ddocs-detail-title" title={docs !== undefined ? t('denable.docs.driverHeading', { name: driverName(docs) }) : t('denable.docs.driverTitle')} onClose={() => setDriverId(undefined)} closeLabel={t('ui.close')} />
            <DrawerBody>
              <DrawerStack>
                {docsError !== undefined && <Notice tone="danger">{docsError}</Notice>}
                {docs === undefined && docsError === undefined && <LoadingBlock lines={2} />}
                {docs !== undefined && (
                  <>
                    <Ink as="p" tone="secondary">
                      {docs.phoneLast4 !== null && <RevealContact userId={docs.driverId} last4={docs.phoneLast4} />}
                    </Ink>
                    <div>{standing(eligibilityRows(docs), docs.types)}</div>
                    {docs.types.length === 0 && <Notice tone="warning">{t('denable.docs.noTypes')}</Notice>}

                    {reviewableTypes(docs).map((type) => {
                      const line = latest.get(type.code);
                      const draft = draftOf(type.code);
                      const id = `doc-${type.code}`;
                      return (
                        <RecordSection key={type.code} role="group" aria-label={kycLabel(type)}>
                          <Row gap={2} align="center" wrap>
                            <strong>{kycLabel(type)}</strong>
                            <Badge tone={type.requiredHere ? 'warning' : 'neutral'}>{v('kyc.requirement_level', type.requiredHere ? 'REQUIRED' : 'OPTIONAL')}</Badge>
                            <Badge>{v('kyc.document_subject', type.subject)}</Badge>
                          </Row>
                          {line === undefined ? (
                            <Text as="p" role="caption" tone="tertiary">{t('denable.docs.noLine')}</Text>
                          ) : (
                            <p>
                              <Badge tone={reviewLineTone(line.status)}>{statusLabel(line.status)}</Badge>{' '}
                              <span>{t('denable.docs.lineSummary', { number: line.numberMasked, validity: validity(line.expiresAt), by: who(line.reviewedBy), at: when(line.reviewedAt ?? line.createdAt) })}</span>
                              {line.reasonCode !== undefined && <Text role="caption" tone="tertiary" block>{v('kyc.evidence_reason', line.reasonCode)}{line.reviewNote !== undefined ? ` — ${line.reviewNote}` : ''}</Text>}
                            </p>
                          )}
                          {mayReview && (
                            <>
                              <FormGrid>
                                {type.rules.number && (
                                  <Field label={t('denable.docs.number')} htmlFor={`${id}-number`}>
                                    <TextInput id={`${id}-number`} className="tnum" value={draft.number} maxLength={DOCUMENT_NUMBER_MAX} autoComplete="off" spellCheck={false} onChange={(e) => setDraft(type.code, { ...draft, number: e.target.value })} />
                                  </Field>
                                )}
                                {type.rules.expiry && (
                                  <Field label={t('denable.docs.validity')} htmlFor={`${id}-validity`}>
                                    <TextInput id={`${id}-validity`} className="tnum" type="date" value={draft.expiryDate} onChange={(e) => setDraft(type.code, { ...draft, expiryDate: e.target.value })} />
                                  </Field>
                                )}
                                {type.rules.expiry && type.validityFromIssue !== undefined && (
                                  <Field label={t('denable.docs.issuedOn')} htmlFor={`${id}-issued`}>
                                    <TextInput id={`${id}-issued`} className="tnum" type="date" value={draft.issueDate} onChange={(e) => setDraft(type.code, { ...draft, issueDate: e.target.value })} />
                                  </Field>
                                )}
                                <Field label={t('denable.docs.result')} htmlFor={`${id}-result`}>
                                  <Select id={`${id}-result`} value={draft.result} onChange={(e) => setDraft(type.code, withDocumentResult(draft, e.target.value as DocumentReviewResult))}>
                                    {DOCUMENT_REVIEW_RESULTS.map((r) => (
                                      <option key={r} value={r}>{v('kyc.document_review_result', r)}</option>
                                    ))}
                                  </Select>
                                </Field>
                                <Field label={t('denable.docs.reason')} htmlFor={`${id}-reason`}>
                                  <Select id={`${id}-reason`} value={draft.reasonCode} onChange={(e) => setDraft(type.code, { ...draft, reasonCode: e.target.value })}>
                                    {documentReasonOptions(draft.result).map((r) => (
                                      <option key={r} value={r}>{v('kyc.evidence_reason', r)}</option>
                                    ))}
                                  </Select>
                                </Field>
                                {type.subject === 'VEHICLE' && (
                                  <Field label={t('denable.docs.vehicle')} htmlFor={`${id}-vehicle`}>
                                    <Select id={`${id}-vehicle`} value={draft.vehicleId} onChange={(e) => setDraft(type.code, { ...draft, vehicleId: e.target.value })} disabled={docs.vehicles.length === 0}>
                                      <option value="">{t(docs.vehicles.length === 0 ? 'denable.docs.noVehicles' : 'denable.docs.chooseVehicle')}</option>
                                      {docs.vehicles.map((v) => (
                                        <option key={v.vehicle.id} value={v.vehicle.id}>{`${v.vehicle.plate} · ${v.vehicle.make} ${v.vehicle.model}`}</option>
                                      ))}
                                    </Select>
                                  </Field>
                                )}
                                <Field label={t(draft.reasonCode === 'OTHER' ? 'denable.docs.noteRequired' : 'denable.docs.note')} htmlFor={`${id}-note`}>
                                  <TextInput id={`${id}-note`} value={draft.note} maxLength={REVIEW_NOTE_MAX} onChange={(e) => setDraft(type.code, { ...draft, note: e.target.value })} />
                                </Field>
                              </FormGrid>
                              {(() => {
                                // Owner answer (PR 291 question 4): the validity counted from the issue date, previewed in the market zone.
                                const until = type.rules.expiry ? documentValidityPreview(type, draft, tz) : undefined;
                                return until !== undefined ? <Text as="p" role="caption" tone="tertiary">{t('denable.docs.validUntilPreview', { date: validity(until) })}</Text> : null;
                              })()}
                              {lineMsg !== undefined && lineMsg.type === type.code && (
                                <div>
                                  <Notice tone={lineMsg.msg.tone}>{lineMsg.msg.text}</Notice>
                                </div>
                              )}
                              <Button variant="secondary" disabled={busy} onClick={() => void record(type)}>{t('denable.docs.record')}</Button>
                            </>
                          )}
                        </RecordSection>
                      );
                    })}

                    <Text as="h2" role="title">{t('denable.docs.history')}</Text>
                    <Table
                      columns={[
                        { key: 'type', header: t('denable.col.type'), render: (l: ReviewedDocumentView) => <span>{typeLabel(l.type)}{l.ownerType === 'vehicle' && <Text role="caption" tone="tertiary" mono block>{plateOf(l.ownerId)}</Text>}</span> },
                        { key: 'status', header: t('denable.col.result'), render: (l: ReviewedDocumentView) => <Badge tone={reviewLineTone(l.status)}>{statusLabel(l.status)}</Badge> },
                        { key: 'number', header: t('denable.docs.number'), render: (l: ReviewedDocumentView) => <span className="tnum">{l.numberMasked}</span> },
                        { key: 'validity', header: t('denable.docs.validity'), render: (l: ReviewedDocumentView) => <span className="tnum">{validity(l.expiresAt)}</span> },
                        { key: 'reason', header: t('denable.docs.reason'), render: (l: ReviewedDocumentView) => <span>{l.reasonCode !== undefined ? v('kyc.evidence_reason', l.reasonCode) : '—'}{l.reviewNote !== undefined && <Text role="caption" tone="tertiary" block>{l.reviewNote}</Text>}</span> },
                        { key: 'who', header: t('denable.col.checkedBy'), render: (l: ReviewedDocumentView) => <span>{who(l.reviewedBy)}<Text role="caption" tone="tertiary" mono block>{when(l.reviewedAt ?? l.createdAt)}</Text></span> },
                      ]}
                      rows={[...docs.lines].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))}
                      rowKey={(l) => l.id}
                      empty={<EmptyCell>{t('denable.docs.historyEmpty')}</EmptyCell>}
                    />
                  </>
                )}
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              <Button variant="ghost" onClick={() => setDriverId(undefined)}>{t('ui.close')}</Button>
            </DrawerFooter>
          </>
        )}
      </Drawer>
    </>
  );
}
